import * as RxDialog from '@radix-ui/react-dialog';
import { can, formatCode, normalizeCode, reasonMessage, testCodeKind } from '@pl/domain';
import type { OrderDetailDto, OrderLineDto, ReasonCode, ScanProgressDto, ScanRequest, ScanResponse } from '@pl/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../../lib/api';
import { CHANNEL_DOT, channelName } from '../../lib/format';
import { qk, useCountingSkus, useMe, useOrder, useSkuMap } from '../../lib/queries';
import { uuid } from '../../lib/uuid';
import { Empty, ErrorNote, RefetchNote, SkuTag, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { useQueue, useQueueState } from '../queue/QueueProvider';
import { RedScreen } from '../RedScreen';
import { announceRefusal, registerRefusalCloser } from '../refusalGuard';
import { type LocalResult, ResultPanel } from '../ResultPanel';
import { feedback } from '../scanner/feedback';
import { ScannerView } from '../scanner/ScannerView';
import { useScanSite } from '../useScanSite';
import { hiddenSkus } from './countKit';
import { orderRef } from './ShipList';
import s from './ShipOrder.module.css';

const CARRIERS = ['UPS', 'FedEx', 'USPS', 'Freight', 'Other'] as const;

/** What a refusal about *this box* is called on the phone; the server's message carries the detail. */
const RED_TITLE: Partial<Record<ReasonCode, string>> = {
  WRONG_SKU: 'Wrong color',
  LINE_COMPLETE: 'Line already complete',
  PICKED_ELSEWHERE: 'On another order',
  ALREADY_SHIPPED: 'Already shipped',
  PALLET_NOT_ALLOWED: 'Scan boxes, not pallets',
  OTHER_SITE: 'Wrong site',
  NOT_RECEIVED: 'Not in stock yet',
  IN_TRANSIT: 'On a transfer',
  WRITTEN_OFF: 'Written off',
  VOIDED: 'Voided sticker',
  UNKNOWN_CODE: 'Unknown sticker',
  INVALID_CODE: 'Unknown sticker',
  TEST_STICKER: 'Test sticker',
};
const redTitle = (reason: ReasonCode | null) => (reason && RED_TITLE[reason]) || "Can't use this box";

/**
 * `POST /scans` never answers a floor rule with an HTTP error — a wrong colour comes back 200 with
 * a reason code. So an HTTP failure here is never about the box: it is the server refusing the
 * whole job, and the next box would be refused in exactly the same way. Those end the screen
 * rather than inviting another scan.
 */
const FATAL: Record<string, { title: string; note: string }> = {
  ORDER_CLOSED: { title: 'Order closed', note: 'Nothing was changed. This order is no longer open for picking.' },
  WRONG_SITE: { title: 'Wrong site', note: 'Nothing was changed. This order ships from another site.' },
  NOT_FOUND: { title: 'Order not found', note: 'Nothing was changed. This order is no longer on the server.' },
  FORBIDDEN: { title: 'Not your site', note: 'Nothing was changed. You do not have access to this order’s site.' },
  // The client mints a fresh id at every capture, so the server can only see a reused one if this
  // app is wrong. Saying "try again" would be a guess; saying what happened is not.
  IDEMPOTENCY_MISMATCH: { title: 'Scan id reused', note: 'Nothing was changed. This is a bug in the app — report it before carrying on.' },
};
const fatalFor = (code: string) => FATAL[code] ?? { title: 'Scan refused', note: 'Nothing was changed. The server would not take this scan.' };

const PUT_BACK = 'Nothing was changed. Put this box back.';

/**
 * Where the one button on a red screen goes. `resume` is a refusal about this box: set it down and
 * scan the next. `orders` is a refusal about the whole job, with nothing to carry on to. `login` is
 * the session gone — every scan after this one would be refused the same way, so the way out is to
 * sign in again, not to be told "Scan refused" and sent to a list that cannot load either.
 */
type RedExit = 'resume' | 'orders' | 'login';
const EXIT_LABEL: Record<RedExit, string> = { resume: 'Got it, put it back', orders: 'Back to orders', login: 'Sign in again' };

interface Red {
  title: string;
  message: string;
  code?: string;
  kicker: string;
  note: string;
  exit: RedExit;
}

/** A scan this screen sent. Its standing is read back off the queue, never cached here. */
interface SentScan {
  clientScanId: string;
  code: string;
  at: number;
}

const lineScanned = (line: OrderLineDto) => line.picked + line.shipped;
const lineLeft = (line: OrderLineDto) => Math.max(0, line.qty - lineScanned(line));
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
/** The one phrase for "a scan for this order is still on the phone", wherever it locks something. */
const waitingFor = (n: number) => `Waiting for ${plural(n, 'scan', 'scans')} to send`;

/**
 * The last scan a box ever gets: every box on the order goes past the phone on its way onto the
 * truck, and only when the server has answered for all of them can the order be confirmed.
 *
 * Two things are load-bearing here. Confirm is locked on the *server's* count of scanned boxes,
 * not on how many times a worker pressed Add — so a scan sitting in the offline queue holds the
 * button down until it has actually landed, and the screen says so in words. And nothing on this
 * screen invents a box: the scanned list is what the server has, with anything still queued shown
 * separately as waiting, because a box that only exists on this phone is not on the truck.
 *
 * And while a count is open at the site, the list names none of the counted SKU's boxes: the count
 * expects that SKU's picked boxes too, and a worker who steps out of it to pick an order would
 * otherwise read codes it is looking for. That line says how many are picked, and that the codes
 * are hidden; a box of it still scans. Until the phone knows what is being counted here, it names
 * no box at all (`hiddenSkus`).
 */
export function ShipOrder() {
  const { orderId = '' } = useParams();
  const me = useMe();
  const { site } = useScanSite();
  const queue = useQueue();
  const qc = useQueryClient();
  const nav = useNavigate();
  const context = `ship:${orderId}`;
  const { pending, settled, version } = useQueueState(context);
  // Asked for again as the screen opens (the settle effect below): box codes are drawn only from
  // that answer or a later one.
  const orderQuery = useOrder(orderId);
  const [openedAt] = useState(() => Date.now());
  const skuById = useSkuMap();
  const counting = useCountingSkus(site?.id);

  const [result, setResult] = useState<LocalResult | null>(null);
  /** Which scan the panel above is describing, so it can follow that scan when it settles. */
  const [resultFor, setResultFor] = useState<string | null>(null);
  const [red, setRed] = useState<Red | null>(null);
  const [sent, setSent] = useState<SentScan[]>([]);
  const [sheet, setSheet] = useState<'confirm' | 'partial' | null>(null);
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [reason, setReason] = useState('');
  /** The server's own answer to the ship, kept locally — the screen after it is not a query. */
  const [shipped, setShipped] = useState<OrderDetailDto | null>(null);

  const carrierId = useId();
  const trackingId = useId();
  const reasonId = useId();

  // `sheet` mirrored into a ref so the refusal closer below — registered once, on mount — always
  // acts on the sheet's current state without re-registering on every open/close.
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;
  // Set only when this sheet is closed *because a refusal arrived*, and read by the sheet's own
  // `onCloseAutoFocus` below. Radix returns focus to whatever was focused when the sheet opened —
  // the Confirm shipped or Ship partial button in the footer — unless that handler prevents it.
  // Left unset, an ordinary close (Cancel, Escape, a successful ship) keeps Radix's normal
  // behaviour, which is exactly what the sheet needs when a refusal is not why it closed.
  const suppressSheetFocus = useRef(false);

  useEffect(
    () =>
      registerRefusalCloser(() => {
        if (sheetRef.current === null) return;
        suppressSheetFocus.current = true;
        setSheet(null);
      }),
    [],
  );

  /**
   * Every place a refusal is shown goes through here, never straight through `setRed`. Closing a
   * sheet and mounting `RedScreen` have to land in the *same* commit: React runs every unmounting
   * effect (which is where a Radix sheet clears its own focus trap) before any newly-mounted one
   * (which is where `RedScreen` takes focus), but only when both updates are batched together. Two
   * separate commits — a sheet's `setSheet(null)` reacting one render behind `RedScreen` mounting —
   * leave that sheet's focus trap alive at the exact moment `RedScreen` tries to take focus, and it
   * pulls focus straight back into the sheet that is a beat away from disappearing out from under it.
   */
  const showRed = useCallback((next: Red) => {
    announceRefusal();
    setRed(next);
  }, []);

  const order = orderQuery.data;
  const ref = order ? orderRef(order) : '';

  /**
   * The server's progress for this order, folded straight into the cached detail so the lines and
   * the lock move the instant a scan the worker is watching is answered. It is only ever a stopgap:
   * the effect below re-asks the server for the order as soon as the scan settles, so the phone
   * ends up on the server's own numbers rather than its arithmetic.
   */
  const applyProgress = useCallback(
    (progress: ScanProgressDto) => {
      qc.setQueryData<OrderDetailDto>(qk.order(orderId), (prev) => {
        if (!prev) return prev;
        const lines = prev.lines.map((line) => {
          const row = progress.lines.find((p) => p.lineId === line.id);
          if (!row) return line;
          const picked = Math.max(0, row.scanned - line.shipped);
          return { ...line, picked, remaining: Math.max(0, line.qty - picked - line.shipped) };
        });
        return { ...prev, lines, scanned: lines.reduce((n, line) => n + lineScanned(line), 0) };
      });
    },
    [orderId, qc],
  );

  /**
   * Every time the server answers a scan for this order, the order is asked for again — whether the
   * worker was watching that scan or not. Most answers arrive where nobody is looking: a scan that
   * queued behind a dead signal lands in a flush seconds or minutes later, with no reply for this
   * screen to read. Without this the phone goes on showing a box short, with the lock down and the
   * pill saying "All synced" beside it, and a worker who believes it fetches a second box of the
   * same cable. A replayed answer's progress can be stale too (docs/api/v1.md, Idempotency), so the
   * order itself is the only number worth ending on. `settled` is read off the queue on every
   * version bump, the same way the lock and the waiting rows are.
   *
   * The baseline starts empty, so opening this screen asks once as well: a scan that landed while
   * the worker was on the order list is already counted when this screen mounts, and the cached
   * order (fresh for 15 s) would otherwise show a box short with Confirm locked, or the reverse.
   */
  const seenSettled = useRef<number | null>(null);
  useEffect(() => {
    if (settled === seenSettled.current) return;
    seenSettled.current = settled;
    void qc.invalidateQueries({ queryKey: qk.order(orderId) });
    void qc.invalidateQueries({ queryKey: ['orders'] });
  }, [settled, orderId, qc]);

  /**
   * A ship whose answer was lost on the way back is retried into a 409 about the ship it has just
   * made. The server is asked what became of the order, and only when it really is out — fully,
   * for a confirm — is that taken as the answer. Anything else (cancelled, or shipped short by
   * someone else) keeps the server's refusal in front of the worker.
   */
  const ship = useCallback(
    async (path: 'confirm-ship' | 'ship-partial', body: object): Promise<OrderDetailDto> => {
      try {
        return await api.post<OrderDetailDto>(`/orders/${orderId}/${path}`, body);
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'ORDER_CLOSED') throw err;
        const now = await api.get<OrderDetailDto>(`/orders/${orderId}`).catch(() => null);
        const out = now?.status === 'shipped' && (path === 'ship-partial' || now.lines.every((line) => line.shipped >= line.qty));
        if (!now || !out) throw err;
        return now;
      }
    },
    [orderId],
  );

  const onShipped = useCallback(
    (data: OrderDetailDto) => {
      setSheet(null);
      setShipped(data);
      qc.setQueryData(qk.order(orderId), data);
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
    [orderId, qc],
  );

  const confirmShip = useMutation({
    mutationFn: (body: { carrier: string | null; tracking: string | null }) => ship('confirm-ship', body),
    onSuccess: onShipped,
  });
  const shipPartial = useMutation({
    mutationFn: (body: { carrier: string | null; tracking: string | null; reason: string }) => ship('ship-partial', body),
    onSuccess: onShipped,
  });
  const unpick = useMutation({
    mutationFn: (code: string) => api.post<ScanProgressDto>(`/orders/${orderId}/unpick`, { code }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.order(orderId) });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  /**
   * The panel is the last scan's standing, not a souvenir of the moment it was taken. An amber
   * "saved on this phone" that stays amber after the scan has landed is the screen telling the
   * worker to worry about a box that is already on the truck.
   */
  const shownResult = useMemo<LocalResult | null>(() => {
    if (!result || !resultFor) return result;
    const item = queue.get(resultFor);
    if (!item || item.status === 'pending') return result;
    // A refusal this screen already showed live keeps the name it was given — the card is what is
    // left of the red screen, and the two must not name the same refusal differently. A scan that
    // was queued and only now comes back refused never had one; the queue kept the server's
    // message but not the code behind it, so the card says what it can stand behind and no more.
    if (item.status === 'failed')
      return result.tone === 'bad' ? result : { tone: 'bad', title: 'Scan refused', message: item.error ?? 'The server would not take this scan.', code: result.code };
    const response = item.response;
    if (!response) return result;
    if (response.outcome === 'rejected') return { tone: 'bad', title: redTitle(response.reasonCode), message: response.message, code: result.code };
    if (response.outcome !== 'accepted') return { tone: 'warn', title: response.message, message: 'Nothing was added for this sticker.', code: result.code };
    return { tone: 'ok', title: response.message, message: `Picked onto ${ref}.`, code: result.code };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, resultFor, queue, ref, version]);

  const handleCode = useCallback(
    async (raw: string) => {
      const code = normalizeCode(raw);
      const kicker = `Not picked · ${ref}`;
      if (!code) {
        // Nothing is sent: the server cannot make sense of this, and a round trip would only delay
        // telling the worker that what they scanned is not one of our stickers.
        feedback('bad');
        setResult(null);
        setResultFor(null);
        if (testCodeKind(raw)) showRed({ title: 'Test sticker', message: reasonMessage('TEST_STICKER'), code: raw.slice(0, 24), kicker, note: PUT_BACK, exit: 'resume' });
        else showRed({ title: 'Unknown sticker', message: "This doesn't look like one of our stickers.", code: raw.slice(0, 24), kicker, note: PUT_BACK, exit: 'resume' });
        return;
      }
      if (!site || !orderId) return;

      const request: ScanRequest = {
        // Made here, at the moment of capture, and reused unchanged on every retry for the life of
        // this scan. That is what stops a resend from picking the box twice.
        clientScanId: uuid(),
        siteId: site.id,
        mode: 'ship',
        contextId: orderId,
        code,
        scannedAt: new Date().toISOString(),
      };
      setSent((prev) => [...prev, { clientScanId: request.clientScanId, code, at: Date.now() }]);
      setResultFor(request.clientScanId);
      const outcome = await queue.submit(request, context);
      const pretty = formatCode(code);

      if (outcome.state === 'queued') {
        feedback('warn');
        // Storage is asked of the queue, not read off the render: the write that decides it
        // happens inside the submit above, so the value this screen rendered with is a scan old.
        if (queue.storageOk()) {
          setResult({ tone: 'warn', title: 'Saved on this phone', message: 'Confirm shipped stays locked until it has sent.', code: pretty });
        } else {
          setResult({ tone: 'warn', title: 'Waiting to send', message: 'This phone could not save it — keep this screen open until it goes.', code: pretty });
        }
        return;
      }

      if (outcome.state === 'failed') {
        feedback('bad');
        if (outcome.error.status === 401) {
          setResult({ tone: 'bad', title: 'Signed out', message: outcome.error.message, code: pretty });
          showRed({ title: 'Signed out', message: outcome.error.message, code: pretty, kicker, note: 'Nothing was changed. Sign in, then scan this box again.', exit: 'login' });
          return;
        }
        const fatal = fatalFor(outcome.error.code);
        setResult({ tone: 'bad', title: fatal.title, message: outcome.error.message, code: pretty });
        showRed({ title: fatal.title, message: outcome.error.message, code: pretty, kicker, note: fatal.note, exit: 'orders' });
        return;
      }

      const response: ScanResponse = outcome.response;
      if (response.outcome === 'rejected') {
        feedback('bad');
        setResult({ tone: 'bad', title: redTitle(response.reasonCode), message: response.message, code: pretty });
        showRed({ title: redTitle(response.reasonCode), message: response.message, code: pretty, kicker, note: PUT_BACK, exit: 'resume' });
        return;
      }
      if (response.outcome === 'duplicate' || response.outcome === 'ignored') {
        feedback('warn');
        setResult({ tone: 'warn', title: response.message, message: 'Nothing was added for this sticker.', code: pretty });
        if (response.progress) applyProgress(response.progress);
        return;
      }

      feedback('ok');
      setResult({ tone: 'ok', title: response.message, message: `Picked onto ${ref}.`, code: pretty });
      if (response.progress) applyProgress(response.progress);
    },
    [applyProgress, context, orderId, queue, ref, showRed, site],
  );

  /**
   * What the server holds, newest first, with anything this screen has sent but not yet had
   * answered shown above it as waiting. A queued scan is deliberately not drawn as a scanned box:
   * it has no undo, because there is nothing on the server to undo yet.
   */
  const rows = useMemo(() => {
    const boxes = [...(order?.boxes ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    const onServer = new Set(boxes.map((box) => box.code));
    const waiting = sent
      .filter((scan) => !onServer.has(scan.code) && queue.get(scan.clientScanId)?.status === 'pending')
      .sort((a, b) => b.at - a.at)
      .map((scan) => ({ key: scan.clientScanId, code: scan.code, skuId: null as string | null, undoable: false, waiting: true }));
    return [...waiting, ...boxes.map((box) => ({ key: box.code, code: box.code, skuId: box.skuId, undoable: box.state === 'picked', waiting: false }))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.boxes, sent, queue, version]);

  if (shipped) {
    return (
      <div className={s.shippedScreen} role="status">
        <div className={s.shippedTick}>
          <Icon name="check" size={58} />
        </div>
        <h2 className={s.shippedTitle}>{`Shipped · ${orderRef(shipped)}`}</h2>
        <p className={s.shippedWho}>{shipped.customerName}</p>
        {(shipped.carrier || shipped.tracking) && (
          <p className={s.shippedMeta}>
            {shipped.carrier ?? 'No carrier'}
            {shipped.tracking && <span className="mono"> · {shipped.tracking}</span>}
          </p>
        )}
        <div className={s.shippedFoot}>
          <Link to="/scan/ship" className={`bigbtn ${s.shippedLink}`}>
            Back to orders
          </Link>
        </div>
      </div>
    );
  }

  // A person with no site has nowhere to ship from. Without this the scanner would take a code and
  // quietly do nothing with it — no sound, no panel — while whatever the panel said last stays up.
  if (!site) {
    return (
      <div className={`scan-body ${s.centre}`}>
        <Empty title="No site on this account" description="Shipping works from a site. Ask an admin to add you to one." />
      </div>
    );
  }

  if (orderQuery.isPending) {
    return (
      <div className={`scan-body ${s.centre}`}>
        <Spinner label="Loading the order" />
      </div>
    );
  }

  // Only an order that never loaded takes the screen. This screen asks for the order again every
  // time a scan settles, so a failed refresh is routine on a patchy signal — and replacing the
  // screen for one took the scanner away mid-order. The order already on screen stays, with a
  // RefetchNote under it.
  if (orderQuery.isLoadingError || !order) {
    return (
      <div className={`scan-body ${s.centre}`}>
        <div className={s.loadFail}>
          <ErrorNote error={orderQuery.error} />
          <button type="button" className={`bigbtn ghost ${s.retryBtn}`} onClick={() => void orderQuery.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const scannedTotal = order.lines.reduce((n, line) => n + lineScanned(line), 0);
  const left = order.lines.reduce((n, line) => n + lineLeft(line), 0);
  const nextLineId = order.lines.find((line) => lineLeft(line) > 0)?.id ?? null;
  const locked = left > 0 || pending > 0;
  // Both reasons, whenever both hold. A scan still on this phone locks Ship partial as well as
  // Confirm, and a partial ship is by definition the case with boxes left — so the waiting line
  // has to show beside "left to scan", not only once nothing is.
  const lockNotes = [left > 0 ? `${plural(left, 'box', 'boxes')} left to scan` : null, pending > 0 ? waitingFor(pending) : null].filter((note): note is string => note !== null);
  const canPartial = Boolean(me.data && can(me.data.role, 'ship_partial')) && scannedTotal > 0;
  // A partial ship closes the order on whatever the server holds right now. A scan still on this
  // phone is a box already on the truck that the server has not heard of: closing the order under
  // it turns that scan into a 409 and leaves the box on the truck and `in_stock` on the server.
  const partialLocked = pending > 0;
  // Said in the sheet too, because the sheet can be open when a scan queues: the camera and a
  // Bluetooth scanner both keep reading behind it.
  const waitingLead = pending > 0 ? ` ${waitingFor(pending)} — shipping now would leave ${pending === 1 ? 'that box' : 'those boxes'} off this order.` : '';
  // The server leaves a counted SKU's codes out and marks its line (`codesHidden`), at the time it
  // answers. Codes are drawn only from an answer to this screen's own request: a cached order from
  // before a count started names boxes it is looking for. What the phone knows of the counts
  // (`hiddenSkus`) hides more, never less.
  const fresh = orderQuery.dataUpdatedAt >= openedAt;
  const hidden = hiddenSkus(counting, site.id);
  const countedHere = new Set(order.lines.filter((line) => line.codesHidden).map((line) => line.skuId));
  const codeHidden = (skuId: string | null) => hidden === 'all' || (skuId !== null && (hidden.has(skuId) || countedHere.has(skuId)));
  // A queued scan keeps its code: it is the box in the worker's hand, and its SKU is not known yet.
  const waitingRows = rows.filter((row) => row.waiting);
  const serverRows = fresh ? rows.filter((row) => !row.waiting && !codeHidden(row.skuId)) : [];
  const hiddenLines = fresh ? order.lines.filter((line) => lineScanned(line) > 0 && codeHidden(line.skuId)) : [];
  const undoable = serverRows.some((row) => row.undoable);
  const busy = confirmShip.isPending || shipPartial.isPending;

  return (
    <>
      <div className={`scan-body pt ${s.orderBody}`}>
        <div className={s.orderHead}>
          <div className={s.orderIdent}>
            <div className={`mono ${s.orderRef}`}>{ref}</div>
            <div className={s.orderWho}>
              <span className="channel">
                <i style={{ background: CHANNEL_DOT[order.channel] }} />
                {channelName(order.channel)}
              </span>
              <span className={s.orderCustomer}>{order.customerName}</span>
            </div>
          </div>
          <div className={s.orderCount}>
            <b>
              {scannedTotal}
              <span>/{order.totalQty}</span>
            </b>
            <small>boxes</small>
          </div>
        </div>
        <RefetchNote queries={[orderQuery]} />
        {counting.isError && hiddenLines.length > 0 && (
          <div className="refetch-note">
            <p role="alert" className="error-note">
              Couldn’t check what is being counted here, so box codes are hidden.
            </p>
            <button type="button" className="btn sm" onClick={() => void counting.refetch()}>
              Try again
            </button>
          </div>
        )}

        <div className={s.lines}>
          {order.lines.map((line) => {
            const done = lineLeft(line) === 0;
            const next = line.id === nextLineId;
            return (
              <div key={line.id} className={`${s.lineRow}${done ? ` ${s.lineDone}` : ''}${next ? ` ${s.lineNext}` : ''}`}>
                <span className={`skux ${s.lineSku}`}>
                  <SkuTag sku={skuById.get(line.skuId)} short />
                  {next && <span className={s.nextTag}>Scan next</span>}
                </span>
                <span className={`mono ${s.lineCount}`}>
                  {lineScanned(line)}/{line.qty}
                </span>
                {done && (
                  <span className={s.lineTick}>
                    <Icon name="check" size={18} />
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* The scanner stops while a red outcome holds the screen, so the next sticker cannot slip
            past unread and the Enter a wedge scanner sends cannot double as a dismissal. */}
        <ScannerView onCode={(raw) => void handleCode(raw)} enabled={red === null} />
        <ResultPanel result={shownResult} />

        {/* The server may have sent none of the boxes (all a counted SKU's): the lines still say how many. */}
        {(rows.length > 0 || hiddenLines.length > 0) && (
          <>
            <div className="sect">
              Scanned · {waitingRows.length + scannedTotal}
              <span className="r">{undoable ? 'Tap × to undo' : 'Newest first'}</span>
            </div>
            <ul className={s.scannedList} aria-label="Scanned boxes">
              {waitingRows.map((row) => (
                <li key={row.key} className={s.scanRow}>
                  {/* A scan still in the queue has no SKU to name — the server has not looked at
                      the sticker yet — so the row shows the code and says it is waiting. */}
                  <span className={`skux ${s.scanSku}`} />
                  <span className={`mono ${s.scanCode}`}>{formatCode(row.code)}</span>
                  <span className={s.scanWait}>
                    <Icon name="wifi-off" size={15} />
                    Waiting
                  </span>
                </li>
              ))}
              {hiddenLines.map((line) => (
                <li key={`line:${line.id}`} className={s.scanRow}>
                  <span className={`skux ${s.scanSku}`}>
                    <SkuTag sku={skuById.get(line.skuId)} short />
                  </span>
                  <span className={s.scanHidden}>
                    <b>{`${lineScanned(line)} of ${line.qty} picked`}</b>
                    <small>{line.codesHidden || (hidden !== 'all' && hidden.has(line.skuId)) ? 'Counting · codes hidden' : 'Codes hidden'}</small>
                  </span>
                </li>
              ))}
              {serverRows.map((row) => (
                <li key={row.key} className={s.scanRow}>
                  <span className={`skux ${s.scanSku}`}>
                    <SkuTag sku={skuById.get(row.skuId ?? '')} short />
                  </span>
                  <span className={`mono ${s.scanCode}`}>{formatCode(row.code)}</span>
                  {row.undoable ? (
                    <button
                      type="button"
                      className={s.undoBtn}
                      aria-label={`Undo ${formatCode(row.code)}`}
                      disabled={unpick.isPending}
                      onClick={() => unpick.mutate(row.code)}
                    >
                      <Icon name="x" size={22} />
                    </button>
                  ) : (
                    <span className={s.scanShipped}>Shipped</span>
                  )}
                </li>
              ))}
            </ul>
            {unpick.isError && <ErrorNote error={unpick.error} />}
          </>
        )}
      </div>

      <div className="scan-foot">
        {canPartial && (
          <button type="button" className={`bigbtn ghost${partialLocked ? ' locked' : ''}`} disabled={partialLocked} onClick={() => setSheet('partial')}>
            {partialLocked && <Icon name="lock" />}
            Ship partial
          </button>
        )}
        {lockNotes.length > 0 && (
          <p className={s.footNote}>
            {lockNotes.map((note) => (
              <span key={note}>{note}</span>
            ))}
          </p>
        )}
        <button type="button" className={`bigbtn${locked ? ' locked' : ''}`} disabled={locked} onClick={() => setSheet('confirm')}>
          {locked && <Icon name="lock" />}
          Confirm shipped
        </button>
      </div>

      <RxDialog.Root
        open={sheet !== null}
        onOpenChange={(open) => {
          if (open || busy) return;
          setSheet(null);
          confirmShip.reset();
          shipPartial.reset();
        }}
      >
        <RxDialog.Portal>
          <RxDialog.Overlay className={s.shipScrim} />
          <RxDialog.Content
            className={s.shipSheet}
            aria-describedby={undefined}
            onCloseAutoFocus={(e) => {
              if (!suppressSheetFocus.current) return;
              e.preventDefault();
              suppressSheetFocus.current = false;
            }}
          >
            <RxDialog.Title className={s.shipSheetTitle}>{sheet === 'partial' ? 'Ship partial' : 'Confirm shipped'}</RxDialog.Title>
            <p className={s.shipSheetLead}>
              {sheet === 'partial'
                ? `Ships the ${plural(scannedTotal, 'box', 'boxes')} already scanned and closes ${ref}.${left > 0 ? ` The other ${left} ${left === 1 ? 'box is' : 'boxes are'} not shipped.` : ''}${waitingLead}`
                : `Ships all ${plural(scannedTotal, 'box', 'boxes')} on ${ref}. This cannot be undone on the phone.${waitingLead}`}
            </p>

            {sheet === 'partial' && (
              <>
                <label className={s.fieldCap} htmlFor={reasonId}>
                  Reason
                </label>
                <textarea
                  id={reasonId}
                  className={`inp lg ${s.reasonField}`}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder="Why is this order going out short?"
                />
              </>
            )}

            <label className={s.fieldCap} htmlFor={carrierId}>
              Carrier
            </label>
            <select id={carrierId} className={`inp lg ${s.field}`} value={carrier} onChange={(e) => setCarrier(e.target.value)}>
              <option value="">Not given</option>
              {CARRIERS.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>

            <label className={s.fieldCap} htmlFor={trackingId}>
              Tracking
            </label>
            <input
              id={trackingId}
              className={`inp lg ${s.field}`}
              type="text"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              maxLength={100}
              placeholder="Optional"
              autoComplete="off"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
            />

            {/* The server is the one that decides whether this order may ship. When it says no —
                a 409 because the order closed under us, or nothing is scanned — that answer is
                shown here and the sheet stays open. There is no second path around it. */}
            <ErrorNote error={confirmShip.error ?? shipPartial.error} />

            <div className={s.sheetActions}>
              <RxDialog.Close asChild>
                <button type="button" className="bigbtn ghost" disabled={busy}>
                  Cancel
                </button>
              </RxDialog.Close>
              {sheet === 'partial' ? (
                <button
                  type="button"
                  className="bigbtn"
                  disabled={busy || partialLocked || reason.trim().length === 0}
                  onClick={() => shipPartial.mutate({ carrier: carrier || null, tracking: tracking.trim() || null, reason: reason.trim() })}
                >
                  Confirm partial ship
                </button>
              ) : (
                <button type="button" className="bigbtn" disabled={busy || locked} onClick={() => confirmShip.mutate({ carrier: carrier || null, tracking: tracking.trim() || null })}>
                  Confirm
                </button>
              )}
            </div>
          </RxDialog.Content>
        </RxDialog.Portal>
      </RxDialog.Root>

      {red && (
        <RedScreen
          title={red.title}
          message={red.message}
          code={red.code}
          kicker={red.kicker}
          note={red.note}
          continueLabel={EXIT_LABEL[red.exit]}
          onContinue={() => {
            setRed(null);
            if (red.exit === 'orders') nav('/scan/ship');
            // Asking again who this is lets the auth guard see the 401 and take the phone to sign-in.
            // Navigating there directly would be bounced straight back by the identity still cached.
            if (red.exit === 'login') void qc.invalidateQueries({ queryKey: qk.me });
          }}
        />
      )}
    </>
  );
}
